import { useTranslation } from "react-i18next";
import { useMatch, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Images, SVGs } from "../assets";
import { useAuthStore } from "../stores";
import { Constants } from "../utils";
import Button from "./Button";
import LanguagePicker, { type LanguagePickerOption } from "./LanguagePicker";
import Modal, { type ModalRef } from "./Modal";
import Popover from "./Popover";
import Text from "./Text";
import * as React from "react";

export type HeaderProps = {
  brand?: string;
  onLogin?: () => void;
  className?: string;
};

export type AppUiLocale = "en" | "cz";

export const tabRoutes = (t: (key: string) => string) => [
  {
    name: "dashboard",
    path: Constants.RoutePath.Dashboard,
    options: {
      title: t("general.operatorConsole"),
    },
    routes: [
      {
        name: "booking",
        path: Constants.RoutePath.Booking,
        options: {
          title: t("general.booking"),
        },
      },
      {
        name: "payment",
        path: Constants.RoutePath.BookingPayment,
        options: {
          title: t("general.payment"),
        },
      },
    ],
  },
  {
    name: "bookings",
    path: Constants.RoutePath.ManageBookings,
    options: {
      title: t("header.manageBookings"),
    },
    routes: [
      {
        name: "booking-detail",
        path: Constants.RoutePath.BookingDetail,
        options: {
          title: t("header.manageBookings"),
        },
      },
    ],
  },
];

function Nav() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isManageBookingsActive = Boolean(
    useMatch({ path: Constants.RoutePath.ManageBookings, end: false }),
  );
  const activeNavPath = isManageBookingsActive
    ? Constants.RoutePath.ManageBookings
    : Constants.RoutePath.Dashboard;

  return (
    <nav className="flex items-center gap-[24px]">
      {tabRoutes(t).map((item) => (
        <Text
          key={item.path}
          children={item.options.title}
          onClick={() => navigate(item.path)}
          className={twMerge(
            "cursor-pointer transition-colors border-b-2 border-transparent pb-[3px]",
            item.path === activeNavPath && "border-text-primary font-semibold",
          )}
        />
      ))}
    </nav>
  );
}

export const LANGUAGE_PICKER_OPTIONS: LanguagePickerOption<AppUiLocale>[] = [
  {
    code: "en",
    flag: SVGs.flagUK,
    label: "English",
  },
  // {
  //   code: "cz",
  //   flag: SVGs.flagCZ,
  //   label: "Čeština",
  // },
];

export default function Header({ className }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const logoutModalRef = React.useRef<ModalRef | null>(null);

  const renderLogoutButton = ({ className = "" }) => {
    return (
      <Button
        color="secondaryDark"
        children={t("general.logout")}
        className={twMerge(
          "hidden rounded-[2px] px-[7px] sm:inline-flex",
          className,
        )}
        onClick={() => logoutModalRef.current?.open()}
      />
    );
  };

  const renderLogo = ({
    src,
    className = "",
  }: {
    src: string;
    className?: string;
  }) => (
    <img
      alt="Health 4 Travel"
      src={src}
      onClick={() => navigate(Constants.RoutePath.Dashboard)}
      className={twMerge("cursor-pointer", className)}
    />
  );

  return (
    <header
      className={twMerge(
        "sticky top-0 z-[100] h-[80px] border-b border-background bg-surface text-text-primary opacity-100",
        className,
      )}
    >
      <div className="flex h-full w-full items-center justify-between gap-6 px-[16px] lg:px-[80px] py-[20px]">
        <div className="flex items-center gap-[32px]">
          {renderLogo({
            src: SVGs.logoRound,
            className: "h-[40px] w-[40px] sm:hidden",
          })}
          {renderLogo({
            src: SVGs.logo,
            className: "hidden h-[25.8px] w-auto sm:block",
          })}
          <Nav />
        </div>
        <div className="flex items-center gap-[16px]">
          {renderLogoutButton({ className: "hidden sm:inline-flex h-[22px]" })}
          <Popover
            align="end"
            content={
              <div>
                <Text level={4}>{user?.name}</Text>
                {user?.email && (
                  <Text level={5} className="text-text-secondary mt-[6px]">
                    {user.email}
                  </Text>
                )}
                {renderLogoutButton({
                  className: "inline-flex w-full sm:hidden mt-[10px]",
                })}
              </div>
            }
          >
            <img
              src={Images.avatar}
              alt={user?.name}
              className="h-[24px] w-[24px] rounded-full shrink-0"
            />
          </Popover>
          <LanguagePicker
            options={LANGUAGE_PICKER_OPTIONS}
            value={i18n.resolvedLanguage ?? LANGUAGE_PICKER_OPTIONS[0].code}
            onChange={(code) => {
              void i18n.changeLanguage(code);
            }}
          />
        </div>
      </div>
      <Modal ref={logoutModalRef} title="Logout">
        <Modal.Confirmation
          description="Are you sure to logout?"
          buttons={[
            {
              children: "No",
              color: "secondary",
              variant: "outline",
              onClick: () => logoutModalRef.current?.close(),
            },
            {
              children: "Yes",
              color: "secondaryDark",
              onClick: () => {
                logoutModalRef.current?.close();
                logout();
              },
            },
          ]}
        />
      </Modal>
    </header>
  );
}
